// src/components/Enablement/Moodle/Quiz/QuizTimer.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  endTime: number | string | Date;
  onTimeUp?: () => void;
  className?: string;
};

function toEpochMs(value: number | string | Date): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      // Heuristic: treat < 1e12 as seconds, otherwise milliseconds.
      return parsed < 1e12 ? parsed * 1000 : parsed;
    }
    const asDate = new Date(value).getTime();
    return Number.isNaN(asDate) ? Date.now() : asDate;
  }
  // number
  return value < 1e12 ? value * 1000 : value;
}

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

const QuizTimer: React.FC<Props> = ({ endTime, onTimeUp, className }) => {
  const endMs = useMemo(() => toEpochMs(endTime), [endTime]);
  const [remaining, setRemaining] = useState<number>(() => endMs - Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    setRemaining(endMs - Date.now());
    firedRef.current = false;
  }, [endMs]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const left = endMs - Date.now();
      setRemaining(left);
      if (left <= 0 && !firedRef.current) {
        firedRef.current = true;
        onTimeUp?.();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [endMs, onTimeUp]);

  const label = remaining <= 0 ? '0:00' : formatDuration(remaining);

  return (
    <div className={className} aria-live="polite" style={{ fontWeight: 600 }}>
      Time remaining: {label}
    </div>
  );
};

export default QuizTimer;